import { MapPin, Edit2, Trash2, Video, Eye } from 'lucide-react';
import { devLog } from '../utils/logger';

interface ProjectCardWithActionsProps {
  post: {
    id: string;
    title: string;
    description: string;
    imageUrl?: string;
    imageUrls?: string[];
    videoUrl?: string;
    videoId?: string;
    user?: {
      name: string;
      location?: string;
      county?: string;
    };
    createdAt: string;
    views?: number;
    coverImageUrl?: string;
    isDraft?: boolean;
    isFeaturedPublic?: boolean;
  };
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onSetPublic?: () => void;
  isOwnProfile?: boolean;
  accessToken?: string | null; // ✅ Keep for future use (not used in card, passed through to PostDetailView)
}

export function ProjectCardWithActions({ 
  post, 
  onView, 
  onEdit, 
  onDelete, 
  onSetPublic,
  isOwnProfile = false,
  accessToken, // ✅ Accepted but not used - prevents prop warnings
}: ProjectCardWithActionsProps) {
  // Support both old single image and new multiple images
  const images = post.imageUrls || (post.imageUrl ? [post.imageUrl] : []);
  const hasVideo = !!post.videoUrl || !!post.videoId;

  return (
    <div className="bg-white rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 border border-gray-200 relative group transform-gpu">
      {/* Action buttons for own profile (same setup for both) */}
      {isOwnProfile && (
        <div className="absolute top-2 right-2 z-20 flex gap-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
            className="p-2 bg-white/95 backdrop-blur-sm rounded-lg shadow-md hover:bg-purple-50 transition-all duration-150 active:scale-95"
            title="Rediger prosjekt"
          >
            <Edit2 className="w-4 h-4 text-purple-600" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              console.log('--- DELETE CLICK TRIGGERED ---');
              devLog('Delete Action', { postId: post.id });
              e.stopPropagation();
              onDelete?.();
            }}
            className="p-2 bg-white/95 backdrop-blur-sm rounded-lg shadow-md hover:bg-red-50 transition-all duration-150 active:scale-95"
            title="Slett prosjekt"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
          {onSetPublic && !post.isDraft && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSetPublic();
              }}
              className="p-2 bg-white/95 backdrop-blur-sm rounded-lg shadow-md hover:bg-blue-50 transition-all duration-150 active:scale-95"
              title={post.isFeaturedPublic ? 'Dette prosjektet vises offentlig' : 'Sett som offentlig prosjekt'}
            >
              <Eye className={`w-4 h-4 ${post.isFeaturedPublic ? 'text-blue-700' : 'text-blue-500'}`} />
            </button>
          )}
        </div>
      )}

      <div
        className="relative z-0 cursor-pointer"
        onClick={onView}
      >
        {/* Media Section - Using cover image */}
        {post.coverImageUrl ? (
          <div className="aspect-[4/3] sm:aspect-video w-full overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 relative">
            <img
              src={post.coverImageUrl}
              alt={post.title}
              className="w-full h-full object-cover"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            />
            {/* Badges for additional content */}
            <div className="absolute bottom-2 right-2 flex flex-wrap gap-2 justify-end">
              {post.isDraft && (
                <div className="px-2.5 py-1 bg-amber-500/90 backdrop-blur-sm text-white rounded-lg text-xs font-medium">
                  Utkast
                </div>
              )}
              {isOwnProfile && post.isFeaturedPublic && (
                <div className="px-2.5 py-1 bg-blue-600/90 backdrop-blur-sm text-white rounded-lg text-xs font-medium">
                  Vises offentlig
                </div>
              )}
              {hasVideo && (
                <div className="px-2.5 py-1 bg-orange-600/90 backdrop-blur-sm text-white rounded-lg text-xs font-medium flex items-center gap-1">
                  <Video className="w-3.5 h-3.5" />
                  Video
                </div>
              )}
              {images.length > 0 && (
                <div className="px-2.5 py-1 bg-black/70 backdrop-blur-sm text-white rounded-lg text-xs font-medium">
                  +{images.length} {images.length === 1 ? 'tilleggsbilde' : 'tilleggsbilder'}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="aspect-[4/3] sm:aspect-video w-full overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100 relative flex items-center justify-center">
            <p className="text-sm text-gray-500">Ingen cover-bilde</p>
          </div>
        )}

        {/* Content Section */}
        <div className="p-3 sm:p-4">
          {/* Title */}
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1.5 line-clamp-1">
            {post.title}
          </h3>
          
          {/* Description */}
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {post.description}
          </p>

          {/* User info - Compact */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center min-w-0 flex-1">
              <span className="text-sm text-gray-700 truncate">{post.user?.name}</span>
            </div>
            {(post.user?.location || post.user?.county) && (
              <div className="flex items-center gap-1 text-gray-500 flex-shrink-0 ml-2">
                <MapPin className="w-3.5 h-3.5" />
                <span className="text-xs hidden sm:inline">{post.user?.location || post.user?.county}</span>
              </div>
            )}
          </div>

          {/* Statistics - Only visible on own profile */}
          {isOwnProfile && (
            <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">{post.views || 0}</span>
                <span className="text-xs text-gray-500 hidden sm:inline">visninger</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
